import { useState } from "react";
import { trpc } from "../lib/trpc";
import { useLanguage } from "../contexts/LanguageContext";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input, Select } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { Power, PowerOff, Building2, Truck, Phone, Mail } from "lucide-react";
import { REGIONS, type Region } from "../../../shared/const";
import { REGION_LOC } from "./admin/base-data-shared";
import { fmtCurrency } from "../lib/format";

export default function Parties() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const agenciesQ = trpc.party.listAgencies.useQuery();
  const fleetsQ = trpc.party.listFleets.useQuery();

  // 20260617 反饋 B4：地區篩選（前端過濾，同時作用於需求方 / 供應方兩個分頁與計數）
  const [fRegion, setFRegion] = useState<"all" | Region>("all");
  const inRegion = (p: { region: string }) => fRegion === "all" || p.region === fRegion;
  const agencies = (agenciesQ.data ?? []).filter(inRegion);
  const fleets = (fleetsQ.data ?? []).filter(inRegion);

  const suspendAgency = trpc.party.suspendAgency.useMutation({ onSuccess: () => agenciesQ.refetch() });
  const activateAgency = trpc.party.activateAgency.useMutation({ onSuccess: () => agenciesQ.refetch() });
  const suspendFleet = trpc.party.suspendFleet.useMutation({ onSuccess: () => fleetsQ.refetch() });
  const activateFleet = trpc.party.activateFleet.useMutation({ onSuccess: () => fleetsQ.refetch() });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "合作方", "en-US": "Parties", "zh-CN": "合作方" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({ "zh-TW": "查看所有旅行社與車隊；管理員可停用 / 啟用。", "en-US": "Agencies and fleets; admin can suspend / activate.", "zh-CN": "查看所有旅行社与车队；管理员可停用 / 启用。" })}
        </p>
      </div>

      <Tabs defaultValue="agencies">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="agencies">
              <Building2 className="mr-1 h-3.5 w-3.5" />
              {t({ "zh-TW": "需求方", "en-US": "Agencies", "zh-CN": "需求方" })} ({agencies.length})
            </TabsTrigger>
            <TabsTrigger value="fleets">
              <Truck className="mr-1 h-3.5 w-3.5" />
              {t({ "zh-TW": "供應方", "en-US": "Fleets", "zh-CN": "供应方" })} ({fleets.length})
            </TabsTrigger>
          </TabsList>
          <Select className="w-36" value={fRegion} onChange={(e) => setFRegion(e.target.value as "all" | Region)}>
            <option value="all">{t({ "zh-TW": "全部區域", "en-US": "All regions", "zh-CN": "全部区域" })}</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {t(REGION_LOC[r])}
              </option>
            ))}
          </Select>
        </div>

        <TabsContent value="agencies">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {agencies.map((a) => (
              <Card key={a.id}>
                <CardHeader className="space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{a.companyName}</CardTitle>
                    <Badge variant={a.status === "active" ? "success" : a.status === "suspended" ? "destructive" : "outline"} size="sm">
                      {a.status}
                    </Badge>
                  </div>
                  <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                    {a.region === "taiwan" ? "TW" : "HK"} · ID {a.id}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-[13px]">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Mail className="h-3 w-3" /> {a.userEmail}
                  </div>
                  {a.phone && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Phone className="h-3 w-3" /> {a.phone}
                    </div>
                  )}
                  {a.contactPerson && (
                    <p className="text-[12px] text-muted-foreground">
                      {t({ "zh-TW": "聯絡人：", "en-US": "Contact: ", "zh-CN": "联系人：" })}{a.contactPerson}
                    </p>
                  )}
                  <CreditLimitEditor
                    agencyId={a.id}
                    current={a.creditLimit}
                    canEdit={isAdmin}
                    onSaved={() => agenciesQ.refetch()}
                  />
                  {isAdmin && (
                    <div className="pt-2">
                      {a.status === "active" ? (
                        <Button variant="outline" size="sm" onClick={() => suspendAgency.mutate({ id: a.id })}>
                          <PowerOff className="h-3 w-3" />
                          {t({ "zh-TW": "停用", "en-US": "Suspend", "zh-CN": "停用" })}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => activateAgency.mutate({ id: a.id })}>
                          <Power className="h-3 w-3" />
                          {t({ "zh-TW": "啟用", "en-US": "Activate", "zh-CN": "启用" })}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="fleets">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {fleets.map((f) => (
              <Card key={f.id}>
                <CardHeader className="space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{f.companyName}</CardTitle>
                    <Badge variant={f.status === "active" ? "success" : f.status === "suspended" ? "destructive" : "outline"} size="sm">
                      {f.status}
                    </Badge>
                  </div>
                  <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                    {f.region === "taiwan" ? "TW" : "HK"} · {f.payoutCurrency} · ID {f.id}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-[13px]">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Mail className="h-3 w-3" /> {f.userEmail}
                  </div>
                  {f.phone && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Phone className="h-3 w-3" /> {f.phone}
                    </div>
                  )}
                  {f.contactPerson && (
                    <p className="text-[12px] text-muted-foreground">
                      {t({ "zh-TW": "聯絡人：", "en-US": "Contact: ", "zh-CN": "联系人：" })}{f.contactPerson}
                    </p>
                  )}
                  {isAdmin && (
                    <div className="pt-2">
                      {f.status === "active" ? (
                        <Button variant="outline" size="sm" onClick={() => suspendFleet.mutate({ id: f.id })}>
                          <PowerOff className="h-3 w-3" />
                          {t({ "zh-TW": "停用", "en-US": "Suspend", "zh-CN": "停用" })}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => activateFleet.mutate({ id: f.id })}>
                          <Power className="h-3 w-3" />
                          {t({ "zh-TW": "啟用", "en-US": "Activate", "zh-CN": "启用" })}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** D2：旅行社信用額度（HKD）。canEdit（僅管理員）為真時可調整；否則唯讀顯示。留空儲存＝清除為未設限。 */
function CreditLimitEditor({
  agencyId,
  current,
  canEdit,
  onSaved,
}: {
  agencyId: number;
  current: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current ?? "");
  const m = trpc.party.setAgencyCreditLimit.useMutation({
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  const label = t({ "zh-TW": "信用額度", "en-US": "Credit limit", "zh-CN": "信用额度" });
  const shown =
    current != null ? fmtCurrency(current, "HKD") : t({ "zh-TW": "未設限", "en-US": "None", "zh-CN": "未设限" });

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-2 text-[12px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="flex items-center gap-2">
          <span className="numeric font-mono">{shown}</span>
          {canEdit && (
            <button
              type="button"
              className="text-primary transition-colors hover:underline"
              onClick={() => {
                setVal(current ?? "");
                setEditing(true);
              }}
            >
              {t({ "zh-TW": "調整", "en-US": "Edit", "zh-CN": "调整" })}
            </button>
          )}
        </span>
      </div>
    );
  }

  const invalid = val.trim() !== "" && Number(val) < 0;
  return (
    <div className="space-y-1.5 border-t border-border/60 pt-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px] text-muted-foreground">HKD</span>
        <Input
          type="number"
          min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="h-8"
          placeholder={t({ "zh-TW": "留空＝未設限", "en-US": "Empty = none", "zh-CN": "留空＝未设限" })}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={m.isPending || invalid}
          onClick={() => m.mutate({ id: agencyId, creditLimit: val.trim() === "" ? null : Number(val) })}
        >
          {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
        </Button>
        {m.error && <span className="text-[12px] text-destructive">{m.error.message}</span>}
      </div>
    </div>
  );
}
